import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Code, Globe, ExternalLink, Github, ChevronRight, 
  ChevronLeft, Tag, Calendar, Search, Layers
} from 'lucide-react';

// Import project images
import projectsData from '../../data/projects.json';
import pic6 from '../../images/projectimg/jhd.webp'
import pic5 from '../../images/projectimg/cdl.webp'
import pic4 from '../../images/projectimg/gentevt.webp'
import pic7 from '../../images/projectimg/ec.webp'
import pic8 from '../../images/projectimg/bob.webp'
import pic1 from '../../images/projectimg/isy.webp'
import pic2 from '../../images/projectimg/gerania.webp'
import pic3 from '../../images/projectimg/scoutsweb.webp'
import pic0 from '../../images/projectimg/flatflow.webp';

// Import UI components
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';

const projectImages = [pic0,pic1, pic2, pic3, pic4, pic5, pic6, pic7, pic8];

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1,
    transition: { 
      staggerChildren: 0.1 
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.5 }
  }
};

// Main component
export default function Projects() {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [projects, setProjects] = useState(projectsData.projects);
  const [selectedProject, setSelectedProject] = useState(null);
  
  // Extract unique categories from project data
  const categories = ['all', ...new Set(projectsData.projects.flatMap(project => 
    project.technologies ? project.technologies : []
  ))];

  useEffect(() => {
    // Filter projects based on selected category and search query
    const filtered = projectsData.projects.filter(project => {
      const matchesCategory = selectedCategory === 'all' || 
        (project.technologies && project.technologies.includes(selectedCategory));
      
      const matchesSearch = project.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
        project.description.toLowerCase().includes(searchQuery.toLowerCase());
      
      return matchesCategory && matchesSearch;
    });
    
    setProjects(filtered);
  }, [selectedCategory, searchQuery]);

  // Scroll back to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-white dark:bg-zinc-900 min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden bg-gradient-to-b from-blue-50/80 to-white dark:from-blue-900/20 dark:to-zinc-900">
        {/* Decorative elements */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-20 -left-10 w-72 h-72 bg-blue-200/10 dark:bg-blue-400/5 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-20 right-10 w-80 h-80 bg-indigo-200/10 dark:bg-indigo-400/5 rounded-full blur-3xl"></div>
          <svg className="absolute right-0 top-0 h-full text-zinc-200 dark:text-zinc-800/20 opacity-10" viewBox="0 0 100 100" preserveAspectRatio="none" fill="currentColor">
            <polygon points="0,0 100,0 100,100" />
          </svg>
        </div>
        
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300 mb-4 px-3 py-1">
              <span className="flex items-center gap-1.5">
                <Layers className="h-3.5 w-3.5" />
                <span>Portfolio</span>
              </span>
            </Badge>
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-3xl sm:text-4xl font-bold text-zinc-900 dark:text-white mb-6"
            >
              My <span className="text-blue-600 dark:text-blue-400">Projects</span>
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-lg text-zinc-700 dark:text-zinc-300"
            >
              Browse through my collection of projects showcasing my skills, creativity, and problem-solving abilities.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Search and Filter Section */}
      <section className="py-8 border-b border-zinc-100 dark:border-zinc-800  top-0 bg-white/80 dark:bg-zinc-900/80 backdrop-blur-lg z-10">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-4 justify-between items-center">
            {/* Search */}
            <div className="relative w-full md:w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-zinc-400 h-4 w-4" />
              <Input 
                type="text" 
                placeholder="Search projects..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 border border-zinc-200 dark:border-zinc-700 rounded-lg bg-white dark:bg-zinc-800 w-full"
              />
            </div>
            
            {/* Categories */}
            <div className="flex flex-wrap gap-2 justify-center md:justify-end w-full md:w-auto">
              {categories.map(category => (
                <Button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  variant={selectedCategory === category ? "default" : "outline"}
                  className={`text-sm rounded-full ${
                    selectedCategory === category 
                      ? "bg-blue-600 text-white hover:bg-blue-700" 
                      : "border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300"
                  }`}
                  size="sm"
                >
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-16">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
          {projects.length > 0 ? (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {projects.map((project) => (
                <ProjectCard 
                  key={project.id} 
                  project={project} 
                  image={projectImages[project.id]} 
                  onClick={() => setSelectedProject(project)}
                />
              ))}
            </motion.div>
          ) : (
            <div className="text-center py-20">
              <div className="mb-4 text-zinc-400 dark:text-zinc-500">
                <Search className="h-12 w-12 mx-auto" />
              </div>
              <h3 className="text-xl font-semibold text-zinc-900 dark:text-white mb-2">No projects found</h3>
              <p className="text-zinc-600 dark:text-zinc-400">Try adjusting your search or filter criteria.</p>
              <Button 
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory('all');
                }}
                variant="outline"
                className="mt-4"
              >
                Clear filters
              </Button>
            </div>
          )}
        </div>
      </section>
      
      {/* Project Detail Modal */}
      <AnimatePresence>
        {selectedProject && (
          <ProjectDetailModal 
            project={selectedProject} 
            image={projectImages[selectedProject.id]} 
            onClose={() => setSelectedProject(null)} 
          />
        )}
      </AnimatePresence>
    </div>
  );
}

const ProjectCard = ({ project, image, onClick }) => {
  console.log("ProjectCard rendered for:", project);
  return (
    <motion.div
      variants={itemVariants}
      whileHover={{ y: -5, transition: { duration: 0.2 } }}
      className="h-full"
    >
      <Card 
        className="overflow-hidden border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-800/90 h-full shadow-sm hover:shadow-lg transition-all duration-300 cursor-pointer"
        onClick={onClick}
      >
        {/* Project Image */}
        <div className="relative h-48 overflow-hidden">
          <img
            src={image}
            alt={project.altTag || project.name}
            className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
          />
          {/* Technologies badges */}
          <div className="absolute bottom-0 left-0 w-full p-2 bg-gradient-to-t from-zinc-900/80 to-transparent">
            <div className="flex flex-wrap gap-1">
              {project.technologies && project.technologies.slice(0, 3).map((tech, index) => (
                <Badge key={index} className="bg-white/20 text-white text-xs backdrop-blur-sm">
                  {tech}
                </Badge>
              ))}
              {project.technologies && project.technologies.length > 3 && (
                <Badge className="bg-white/20 text-white text-xs backdrop-blur-sm">
                  +{project.technologies.length - 3}
                </Badge>
              )}
            </div>
          </div>
        </div>
        
        <CardContent className="p-5">
          <h3 className="font-bold text-lg text-zinc-900 dark:text-zinc-100 mb-2 line-clamp-1">
            {project.name}
          </h3>
          
          <p className="text-zinc-600 dark:text-zinc-400 text-sm mb-4 line-clamp-2">
            {project.description}
          </p>
          
          <div className="mt-auto pt-2 flex items-center justify-between">
            <div className="text-xs text-zinc-500 dark:text-zinc-500 flex items-center">
              <Calendar className="h-3 w-3 mr-1" />
              {project.date || "2023"}
            </div>
            <Button variant="ghost" size="sm" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 p-0 h-auto">
              <span className="flex items-center">
                View Details
                <ChevronRight className="h-3.5 w-3.5 ml-1" />
              </span>
            </Button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

const ProjectDetailModal = ({ project, image, onClose }) => {
  // Prevent scrolling when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 50 }}
        transition={{ type: 'spring', damping: 25 }}
        className="bg-white dark:bg-zinc-900 w-full max-w-3xl max-h-[85vh] overflow-y-auto rounded-xl shadow-xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Project Header Image */}
        <div className="relative h-60 sm:h-72 overflow-hidden">
          <img
            src={image}
            alt={project.name}
            className="w-full h-full object-cover"
          />
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 bg-black/40 text-white rounded-full p-2 hover:bg-black/60 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div className="absolute bottom-0 left-0 w-full bg-gradient-to-t from-black/80 to-transparent p-6">
            <h2 className="text-2xl sm:text-3xl font-bold text-white">{project.name}</h2>
          </div>
        </div>
        
        {/* Project Content */}
        <div className="p-6 sm:p-8">
          {/* Project Description */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-zinc-900 dark:text-white mb-3">Project Overview</h3>
            <p className="text-zinc-700 dark:text-zinc-300 leading-relaxed">
              {project.description}
            </p>
          </div>

          {/* Project Details */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
            {/* Technology Stack */}
            <div className="space-y-2">
              <h4 className="font-medium text-zinc-900 dark:text-white flex items-center gap-2">
                <Code className="h-4 w-4 text-blue-600" />
                Technology Stack
              </h4>
              <div className="flex flex-wrap gap-2">
                {project.technologies ? (
                  project.technologies.map((tech, index) => (
                    <Badge key={index} variant="outline" className="border-zinc-200 dark:border-zinc-700 px-2 py-1 text-xs">
                      {tech}
                    </Badge>
                  ))
                ) : (
                  <span className="text-zinc-500 dark:text-zinc-400 text-sm">Information not available</span>
                )}
              </div>
            </div>
            
            {/* Project Date */}
            <div className="space-y-2">
              <h4 className="font-medium text-zinc-900 dark:text-white flex items-center gap-2">
                <Calendar className="h-4 w-4 text-blue-600" />
                Project Timeline
              </h4>
              <p className="text-zinc-700 dark:text-zinc-300">
                {project.date || "2023"}
              </p>
            </div>
          </div>
          
          {/* Project Tags */}
          {project.tags && (
            <div className="mb-6">
              <h4 className="font-medium text-zinc-900 dark:text-white flex items-center gap-2 mb-2">
                <Tag className="h-4 w-4 text-blue-600" />
                Tags
              </h4>
              <div className="flex flex-wrap gap-2">
                {project.tags.map((tag, index) => (
                  <Badge key={index} className="bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300">
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          )}
          
          {/* Project Links */}
          <div className="pt-4 border-t border-zinc-200 dark:border-zinc-800 flex flex-wrap gap-3">
            {project.linkfromweb && (
              <Button asChild className="bg-blue-600 hover:bg-blue-700 text-white">
                <a 
                  href={project.linkfromweb} 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2"
                >
                  <Globe className="h-4 w-4" />
                  Visit Live Site
                </a>
              </Button>
            )}
            
            {project.github && (
              <Button asChild variant="outline">
                <a 
                  href={project.github} 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2"
                >
                  <Github className="h-4 w-4" />
                  View Source Code
                </a>
              </Button>
            )}
            
            {project.otherLink && (
              <Button asChild variant="ghost">
                <a 
                  href={project.otherLink} 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2"
                >
                  <ExternalLink className="h-4 w-4" />
                  More Information
                </a>
              </Button>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};